import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Gift, Link as LinkIcon, RefreshCw } from 'lucide-react';
import { useWebsites } from '@/context/WebsitesContext';

declare global {
  interface Window { supabase: any }
}

const BacklinkRewardsCard: React.FC = () => {
  const { currentWebsite } = useWebsites();
  const [backlinkUrl, setBacklinkUrl] = useState('');
  const [status, setStatus] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Pre-fill with homepage URL for convenience
    if (currentWebsite?.url && !backlinkUrl) {
      try {
        const url = new URL(currentWebsite.url);
        setBacklinkUrl(`${url.origin}`);
      } catch {}
    }
  }, [currentWebsite]);

  const callFunction = async (action: 'enrol' | 'verify' | 'cancel') => {
    if (!currentWebsite?.id) return;
    setIsLoading(true);
    try {
      const { data: sessionData } = await window.supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/backlink-rewards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ website_id: currentWebsite.id, backlink_url: backlinkUrl, action })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Request failed');
      setStatus(JSON.stringify(json, null, 2));
    } catch (e: any) {
      setStatus(`Error: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card id="backlink-rewards">
      <CardHeader>
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          <Gift className="h-5 w-5 text-primary" />
          Get free monthly credits
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Add a backlink to contentgardener.ai on your website. As long as the link is active, you will receive 5 free credits each month.
        </p>
        <div className="flex gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 border rounded px-2 py-1">
              <LinkIcon className="h-4 w-4 text-muted-foreground" />
              <input
                value={backlinkUrl}
                onChange={(e) => setBacklinkUrl(e.target.value)}
                placeholder="https://your-site.com/page-with-backlink"
                className="w-full bg-transparent outline-none text-sm"
              />
            </div>
          </div>
          <Button variant="default" disabled={isLoading || !backlinkUrl} onClick={() => callFunction('enrol')}>
            Enrol
          </Button>
          <Button variant="outline" disabled={isLoading} onClick={() => callFunction('verify')}>
            <RefreshCw className="h-4 w-4 mr-1" /> Verify now
          </Button>
        </div>
        {status && <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-32">{status}</pre>}
      </CardContent>
    </Card>
  );
};

export default BacklinkRewardsCard;


